import {
  BarChart3,
  Bell,
  Bookmark,
  Code2,
  Download,
  Eye,
  Factory,
  History,
  Key,
  Landmark,
  LayoutDashboard,
  ListChecks,
  Package,
  Plug,
  Search,
  SearchCheck,
  Sprout,
  TrendingDown,
  Webhook,
} from 'lucide-react';
import { ADMIN_MENU_ITEMS } from './menu.config';
import type { MenuItem } from './types';

export interface AppNavItem {
  id: string;
  titleKey: string;
  path: string;
  icon: NonNullable<MenuItem['icon']>;
  match?: 'exact' | 'prefix';
  badge?: 'watchlist-unread' | 'notifications-unread' | 'saved-search-new';
  permission?: string;
  hardNavigate?: boolean;
}

export interface AppNavSection {
  id: string;
  titleKey?: string;
  items: AppNavItem[];
  permission?: string;
  collapsible?: boolean;
}

export const APP_NAV_SECTIONS: AppNavSection[] = [
  {
    id: 'primary',
    items: [
      {
        id: 'overview',
        titleKey: 'navigation.overview',
        path: '/overview',
        icon: LayoutDashboard,
        match: 'exact',
      },
      {
        id: 'search',
        titleKey: 'navigation.search',
        path: '/search',
        icon: Search,
        hardNavigate: true,
      },
      {
        id: 'watchlist',
        titleKey: 'navigation.watchlist',
        path: '/watchlist',
        icon: Bookmark,
        badge: 'watchlist-unread',
      },
      {
        id: 'my_lists',
        titleKey: 'navigation.myLists',
        path: '/my-lists',
        icon: ListChecks,
      },
      {
        id: 'saved_searches',
        titleKey: 'navigation.savedSearches',
        path: '/my-lists?tab=searches',
        icon: SearchCheck,
        badge: 'saved-search-new',
      },
      {
        id: 'notifications',
        titleKey: 'navigation.notifications',
        path: '/account/notifications',
        icon: Bell,
        badge: 'notifications-unread',
      },
      {
        id: 'usage',
        titleKey: 'navigation.usage',
        path: '/account/usage',
        icon: BarChart3,
      },
    ],
  },
  {
    id: 'activity',
    titleKey: 'navigation.sectionActivity',
    collapsible: true,
    items: [
      {
        id: 'recent_searches',
        titleKey: 'navigation.recentSearches',
        path: '/activity?tab=searches',
        icon: History,
        match: 'exact',
      },
      {
        id: 'recently_viewed',
        titleKey: 'navigation.recentlyViewed',
        path: '/activity?tab=viewed',
        icon: Eye,
        match: 'exact',
      },
      {
        id: 'downloads',
        titleKey: 'navigation.downloads',
        path: '/activity?tab=downloads',
        icon: Download,
        match: 'exact',
      },
    ],
  },
  {
    id: 'explore',
    titleKey: 'navigation.sectionExplore',
    items: [
      {
        id: 'insolvenzticker',
        titleKey: 'navigation.insolvenzticker',
        path: '/insolvenzticker',
        icon: TrendingDown,
      },
      {
        id: 'neugruendungen',
        titleKey: 'navigation.newCompanies',
        path: '/neugruendungen',
        icon: Sprout,
      },
      {
        id: 'tenders',
        titleKey: 'navigation.tenders',
        path: '/vergabe',
        icon: Landmark,
      },
      {
        id: 'industry',
        titleKey: 'navigation.industry',
        path: '/industry',
        icon: Factory,
      },
    ],
  },
  {
    id: 'developer',
    titleKey: 'navigation.sectionDeveloper',
    collapsible: true,
    items: [
      {
        id: 'api_docs',
        titleKey: 'navigation.apiDocs',
        path: '/developer/apis',
        icon: Code2,
      },
      {
        id: 'sdks',
        titleKey: 'navigation.sdks',
        path: '/developer/sdks',
        icon: Package,
      },
      {
        id: 'api_keys',
        titleKey: 'navigation.apiKeys',
        path: '/account/api-keys',
        icon: Key,
      },
      { id: 'mcp', titleKey: 'navigation.mcpServer', path: '/mcp', icon: Plug },
      {
        id: 'subscriptions',
        titleKey: 'navigation.subscriptionsEvents',
        path: '/developer/subscriptions',
        icon: Webhook,
      },
    ],
  },
  {
    id: 'admin',
    titleKey: 'navigation.admin',
    permission: 'admin.view',
    collapsible: true,
    items: ADMIN_MENU_ITEMS.flatMap((item) =>
      item.path && item.icon
        ? [
            {
              id: item.path.replace(/^\//, '').replace(/\//g, '_'),
              titleKey:
                item.translationKey ??
                `navigation.adminItems.${item.path.split('/').pop() || 'dashboard'}`,
              path: item.path,
              icon: item.icon,
              permission: item.permission,
              match:
                item.path === '/admin'
                  ? ('exact' as const)
                  : ('prefix' as const),
            },
          ]
        : [],
    ),
  },
];

export function isAppNavActive(
  item: AppNavItem,
  pathname: string,
  tab: string | null,
) {
  const path = item.path.split('?')[0];
  const matches =
    pathname === path ||
    (item.match !== 'exact' && pathname.startsWith(path + '/'));
  if (!matches) return false;
  if (item.id === 'saved_searches') return tab === 'searches';
  if (item.id === 'my_lists') return tab !== 'searches';
  if (item.id === 'recent_searches') return tab !== 'viewed' && tab !== 'downloads';
  if (item.id === 'recently_viewed') return tab === 'viewed';
  if (item.id === 'downloads') return tab === 'downloads';
  return true;
}
